import type { Column, ColumnDef } from "@tanstack/react-table";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { AccountsCell } from "@/components/shared/cells/accounts";
import { BookingTextCell } from "@/components/shared/cells/booking-text";
import { CurrencyCell } from "@/components/shared/cells/currency";
import { ExpandRow } from "@/components/shared/cells/expand-row";
import { SelectCell } from "@/components/shared/cells/select";
import { TaxCodeCell } from "@/components/shared/cells/tax-code";
import {
  type InvoiceRowData,
  useTableInstanceContext,
} from "@/components/shared/data-table";
import type { FormValues as AccountFormValues } from "@/components/shared/form/lineItems/account";
import type { FormValues as CostCenterFormValues } from "@/components/shared/form/lineItems/cost-center";
import type { FormValues as TaxCodeFormValues } from "@/components/shared/form/lineItems/taxCode";
import { SpecialFilterValue } from "@/components/shared/invoices-filters/filter/utils";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { confColor } from "@/lib/miscellaneous/confidence-color";
import { getBookingKey } from "@/lib/miscellaneous/group-by-booking";
import { serviceResearchPrompt } from "@/lib/prompts/service";
import { cn } from "@/lib/utils";
import { useGetAllAccounts } from "@/queries/accounts/get-all";
import { useGetCompanyInfo } from "@/queries/companies-info/fetch";
import { useUpdateManyLineItems } from "@/queries/line-items/update";
import { useGetAllTaxCodes } from "@/queries/tax-codes/get-all";

import { CostCenterCell } from "@/components/shared/cells/cost-center";
import { AskAi } from "@/features/ask-ai/ask-ai";

const selectColumn: ColumnDef<any> = {
  id: "select",
  size: 45,
  header: (header) => {
    const onCanSelectRow = (row: any) => {
      return row.depth !== 0;
    };

    return <SelectHeader {...header} canSelectRow={onCanSelectRow} />;
  },
  cell: (cell) => {
    const onCanSelectRow = (row: any) => {
      return row.depth !== 0;
    };

    return (
      <div
        className="flex items-center justify-center w-full"
        style={{ paddingLeft: `${(cell.row.depth - 1) * 2}rem` }}
      >
        <SelectCell {...cell} canSelectRow={onCanSelectRow} />
      </div>
    );
  },
  enableSorting: false,
  enableHiding: false,
};

const expandColumn: ColumnDef<any> = {
  id: "expand",
  size: 60,
  header: () => null,
  cell: ({ row }) => {
    const isBookingGroup = row.depth === 1;

    if (!isBookingGroup) return null;

    return <ExpandRow row={row} />;
  },
};

const supplierColumn: ColumnDef<any> = {
  accessorKey: "supplier.supplierId",
  id: "supplierId",
  size: 200,
  enableResizing: false,
  filterFn: (row, columnId, value) => {
    const supplier = row.original?.supplier;

    if (value === SpecialFilterValue.Unknown) {
      return supplier === null;
    }

    return (
      supplier?.supplierId === value || supplier?.parentSupplierId === value
    );
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Kreditor" />
  ),
  cell: ({ row }) => {
    return null;
  },
};

const lineItemDescriptionColumn: ColumnDef<any> = {
  id: "lineItemDescription",
  accessorKey: "description",
  minSize: 150,
  header: ({ column }: { column: Column<any> }) => {
    const { t } = useTranslation();
    return <DataTableColumnHeader column={column} title={t("booking_text")} />;
  },
  cell: ({ row }) => {
    const { t } = useTranslation();
    const updateLineItems = useUpdateManyLineItems({
      disableOptimisticResponse: true,
    });

    const description = row.original?.description;
    const invoiceDetails = row.original?.invoice;
    const invoiceText = invoiceDetails?.invoiceText ?? "";
    const invoiceIntent = invoiceDetails?.invoiceIntent ?? "";
    const bookingText = row.original?.bookingText ?? "";

    const handleOnChange = async (lineItemIds: string[], text: string) => {
      if (bookingText === text) {
        return;
      }

      const toastId = toast.loading(t("generic_loading"));

      try {
        await updateLineItems(lineItemIds, {
          bookingText: text,
          bookingTextManuallyUpdated: true,
        });
        toast.dismiss(toastId);
      } catch (error) {
        toast.error(t("generic_error"), { id: toastId, richColors: true });
      }
    };

    return (
      <BookingTextCell
        rowId={row.id}
        subRows={row.subRows}
        depth={row.depth - 1}
        readOnly={row.depth > 1}
        description={description}
        invoiceText={invoiceText}
        invoiceIntent={invoiceIntent}
        onChange={handleOnChange}
        toolbarDisabled
      />
    );
  },
};

const actionsColumn: ColumnDef<any> = {
  id: "actions",
  header: () => null,
  size: 43,
  // @ts-ignore
  cell: ({ isHovered, table, row }) => {
    const { t } = useTranslation();
    const { companyInfo } = useGetCompanyInfo();

    const description = row.original?.description;
    const invoiceDetails = row.original?.invoice;
    const invoiceText = invoiceDetails?.invoiceText ?? "";

    const showRowActions = isHovered || table.getFocusedRowId() === row.id;

    return (
      <div
        className={`flex space-x-2 transition-opacity duration-200 ${
          showRowActions ? "opacity-100" : "opacity-0"
        }`}
      >
        <TooltipProvider>
          <div className="flex justify-center gap-1">
            <AskAi
              prompt={serviceResearchPrompt(
                description,
                companyInfo?.companyDescription ?? "not available",
                invoiceText,
              )}
              query={description}
              type="internal"
            >
              {({ askAssistant }) => (
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={(e) => {
                        // this button has priority over anything else.
                        e.stopPropagation();
                        askAssistant();
                      }}
                    >
                      <Search size={16} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("ask_ai.extras.service")}</TooltipContent>
                </Tooltip>
              )}
            </AskAi>
          </div>
        </TooltipProvider>
      </div>
    );
  },
};

const amountColumn: ColumnDef<any> = {
  id: "amount",
  accessorKey: "amount",
  size: 110,
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Betrag" />
  ),
  cell: ({ row }) => {
    const currency = row.original?.invoice?.currency;
    const totalAmount = row.original?.amount;

    return <CurrencyCell value={totalAmount} currency={currency} />;
  },
};

const taxCodeColumn: ColumnDef<any> = {
  accessorKey: "taxCodeId",
  id: "taxCode",
  minSize: 80,
  maxSize: 200,
  filterFn: (row, columnId, value) => {
    SpecialFilterValue.Unknown;
    if (!row.original.expenseAccount?.accountId)
      return value === SpecialFilterValue.Unknown;
    else return row.original.expenseAccount?.accountId === value;
  },
  header: ({ column }) => <DataTableColumnHeader column={column} title="BU" />,
  sortingFn: (a, b) => {
    const aName = a.original?.taxCode?.datevBu ?? "";
    const bName = b.original?.taxCode?.datevBu ?? "";

    if (aName === "" && bName === "") {
      return 0;
    }
    if (aName === "") {
      return 1;
    }
    if (bName === "") {
      return -1;
    }
    return aName.localeCompare(bName);
  },
  cell: ({ row }) => {
    const { taxCodes } = useGetAllTaxCodes();
    const { table, loadingRows } = useTableInstanceContext<InvoiceRowData>();
    const updateLineItems = useUpdateManyLineItems();

    const parentRow = row.getParentRow();

    const lineItemIds = getLineItemIds(row);

    const accountIds = [row.original.accountId];
    const taxCodeIds = [row.original.taxCodeId];

    const taxCode = taxCodes.find(
      (taxCode) => taxCode.taxCodeId === row.original.taxCodeId,
    );

    const tooltipDescription = [
      {
        name: taxCode?.name ?? "",
        id: taxCode?.datevBu ?? "",
        justification: row.original?.buJustification ?? "",
      },
    ];

    const onChange = async (value: TaxCodeFormValues) => {
      const nextBookingKey = getBookingKey({
        accountId: row.original.accountId,
        taxCodeId: value.taxCodeId,
        costCenterId: row.original.costCenterId,
        invoiceId: row.original.invoice.invoiceId,
        createdAt: row.original.createdAt,
        createdBy: row.original.createdBy,
        unlinked: row.original.unlinked,
      });

      let rowWithBooking = null;
      try {
        rowWithBooking = table.getRow(nextBookingKey);
      } catch (error) {
        rowWithBooking = null;
      }

      if (parentRow && parentRow.depth === 1) {
        loadingRows[parentRow.id] = true;
        loadingRows[nextBookingKey] = true;
      } else {
        loadingRows[nextBookingKey] = Boolean(rowWithBooking);
      }

      table.setLoadingRow(() => ({
        ...loadingRows,
      }));

      await updateLineItems(value.lineItemIds, {
        taxCodeId: value.taxCodeId,
      });
    };

    const onError = () => {
      table.setLoadingRow(() => ({}));
    };

    const onSuccess = async () => {
      table.setLoadingRow(() => ({}));
    };

    return (
      <TaxCodeCell
        lineItemIds={lineItemIds}
        accountIds={accountIds}
        taxCodeIds={taxCodeIds}
        onChange={onChange}
        onError={onError}
        onSuccess={onSuccess}
        tooltipDescriptions={tooltipDescription}
        className={cn(confColor(row.original.taxCodeConfidence))}
      />
    );
  },
};

const accountColumn: ColumnDef<any> = {
  accessorKey: "accountId",
  id: "expenseAccount",
  minSize: 150,
  maxSize: 300,
  filterFn: (row, columnId, value) => {
    SpecialFilterValue.Unknown;
    if (!row.original.expenseAccount?.accountId)
      return value === SpecialFilterValue.Unknown;
    else return row.original.expenseAccount?.accountId === value;
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Sachkonto" />
  ),
  sortingFn: (a, b) => {
    const aName = a.original?.expenseAccount?.accountName ?? "";
    const bName = b.original?.expenseAccount?.accountName ?? "";

    if (aName === "" && bName === "") {
      return 0;
    }
    if (aName === "") {
      return 1;
    }
    if (bName === "") {
      return -1;
    }
    return aName.localeCompare(bName);
  },
  cell: ({ row, table }) => {
    const { accounts } = useGetAllAccounts();
    const { loadingRows } = useTableInstanceContext<InvoiceRowData>();
    const updateLineItems = useUpdateManyLineItems({
      disableOptimisticResponse: true,
    });

    const parentRow = row.getParentRow();

    const lineItemIds = getLineItemIds(row);

    const accountIds = [row.original.accountId];

    const account = accounts.find(
      (account) => account.accountId === row.original.accountId,
    );

    const tooltipDescription = [
      {
        name: account?.accountName ?? "",
        number: account?.accountNo ?? "",
        justification: row.original?.accountJustification ?? "",
      },
    ];

    const onChange = async (value: AccountFormValues) => {
      const nextBookingKey = getBookingKey({
        accountId: value.accountId,
        taxCodeId: row.original.taxCodeId,
        costCenterId: row.original.costCenterId,
        invoiceId: row.original.invoice.invoiceId,
        createdAt: row.original.createdAt,
        createdBy: row.original.createdBy,
        unlinked: row.original.unlinked,
      });

      // is there already a booking with the new key?
      let rowWithBooking = null;
      try {
        rowWithBooking = table.getRow(nextBookingKey);
      } catch (error) {
        rowWithBooking = null;
      }

      if (parentRow && parentRow.depth === 1) {
        loadingRows[parentRow.id] = true;
        loadingRows[nextBookingKey] = true;
      } else {
        loadingRows[nextBookingKey] = Boolean(rowWithBooking);
      }

      table.setLoadingRow(() => ({
        ...loadingRows,
      }));

      await updateLineItems(value.lineItemIds, {
        accountId: value.accountId,
      });
    };

    const onError = () => {
      table.setLoadingRow(() => ({}));
    };

    const onSuccess = async () => {
      table.setLoadingRow(() => ({}));
    };

    return (
      <AccountsCell
        lineItemIds={lineItemIds}
        accountIds={accountIds}
        onAccountChange={onChange}
        onSuccess={onSuccess}
        onError={onError}
        tooltipDescription={tooltipDescription}
        className={cn(confColor(row.original.accountConfidence))}
      />
    );
  },
};

const costCenterColumn: ColumnDef<any> = {
  accessorKey: "costCenterId",
  id: "costCenter",
  minSize: 150,
  maxSize: 300,
  filterFn: (row, columnId, value) => {
    SpecialFilterValue.Unknown;
    if (!row.original.costCenterId) return value === SpecialFilterValue.Unknown;
    else return row.original.costCenterId === value;
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Kostenstelle" />
  ),
  sortingFn: (a, b) => {
    const aName = a.original?.costCenter?.name ?? "";
    const bName = b.original?.costCenter?.name ?? "";

    if (aName === "" && bName === "") {
      return 0;
    }
    if (aName === "") {
      return 1;
    }
    if (bName === "") {
      return -1;
    }
    return aName.localeCompare(bName);
  },
  cell: ({ row }) => {
    const { table, loadingRows } = useTableInstanceContext<InvoiceRowData>();
    const updateLineItems = useUpdateManyLineItems();

    const parentRow = row.getParentRow();

    const lineItemIds = getLineItemIds(row);

    const costCenterIds = [row.original.costCenterId];

    const onChange = async (value: CostCenterFormValues) => {
      const nextBookingKey = getBookingKey({
        accountId: row.original.accountId,
        taxCodeId: row.original.taxCodeId,
        costCenterId: value.costCenterId,
        invoiceId: row.original.invoice.invoiceId,
        createdAt: row.original.createdAt,
        createdBy: row.original.createdBy,
        unlinked: row.original.unlinked,
      });

      let rowWithBooking = null;
      try {
        rowWithBooking = table.getRow(nextBookingKey);
      } catch (error) {
        rowWithBooking = null;
      }

      if (parentRow && parentRow.depth === 1) {
        loadingRows[parentRow.id] = true;
        loadingRows[nextBookingKey] = true;
      } else {
        loadingRows[nextBookingKey] = Boolean(rowWithBooking);
      }

      table.setLoadingRow(() => ({
        ...loadingRows,
      }));

      await updateLineItems(value.lineItemIds, {
        costCenterId: value.costCenterId,
      });
    };

    const onError = () => {
      table.setLoadingRow(() => ({}));
    };

    const onSuccess = async () => {
      table.setLoadingRow(() => ({}));
    };

    return (
      <CostCenterCell
        lineItemIds={lineItemIds}
        costCenterIds={costCenterIds}
        onChange={onChange}
        onError={onError}
        onSuccess={onSuccess}
        className={cn(confColor(row.original.costCenterConfidence))}
      />
    );
  },
};

const accountReviewedColumn: ColumnDef<any> = {
  id: "accountReviewed",
  accessorKey: "lineItemDetails.lineItem.accountReviewed",
  filterFn: (row) => {
    const depth = row.depth;

    if (depth === 0) {
      // check if all line items are approved at invoice level
      const isApproved = row.original.lineItems.every(
        (item: any) => item.lineItem.accountReviewed,
      );

      return !isApproved;
    }

    if (depth === 1) {
      // check if all line items are approved at booking level
      const isApproved = row.original.subRows.every(
        (item: any) => item.accountReviewed,
      );

      return !isApproved;
    }

    if (depth === 2) {
      // check if the line item is approved
      return !row.original.accountReviewed;
    }

    return true;
  },
};

const isDuplicateColumn: ColumnDef<any> = {
  accessorKey: "invoice.isDuplicate",
  id: "isDuplicate",
};

const isBookingColumn: ColumnDef<any> = {
  accessorKey: "invoice.isBooking",
  id: "isBooking",
};

const invoiceNoColumn: ColumnDef<any> = {
  accessorKey: "invoice.invoiceNo",
  id: "invoiceNo",
};

const invoiceStatusColumn: ColumnDef<any> = {
  accessorKey: "invoice.status",
  id: "invoiceStatus",
};

export const columns = (showCostCenterColumn: boolean) => [
  invoiceNoColumn,
  selectColumn,
  supplierColumn,
  expandColumn,
  lineItemDescriptionColumn,
  actionsColumn,
  amountColumn,
  taxCodeColumn,
  accountColumn,
  accountReviewedColumn,
  isDuplicateColumn,
  isBookingColumn,
  invoiceStatusColumn,
  ...(showCostCenterColumn ? [costCenterColumn] : []),
];

const getLineItemIds = (row: any) => {
  const parentRow = row.getParentRow();

  if (parentRow?.depth === 0) {
    return row.original.subRows.map((lineItem: any) => lineItem.id);
  }

  if (parentRow?.depth === 1) {
    return [row.original.id];
  }
};
